import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';

const VMContext = createContext(null);
const POLL_INTERVAL = 30000;

export const VMContextProvider = ({ children }) => {
  const [session, setSessionState] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const pollRef = useRef(null);
  const countdownRef = useRef(null);
  const { isAuthenticated } = useAuth();

  const fetchSession = async () => {
    try {
      const data = await apiService.getActiveSession();
      const res = data?.data || data;
      if (res?.running) {
        setSessionState(res);
        setSecondsLeft(s => s > 0 ? s : (res.idle_seconds_remaining ?? 1800));
      } else {
        setSessionState(false);
      }
    } catch {
      setSessionState(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchSession();
    pollRef.current = setInterval(fetchSession, POLL_INTERVAL);
    return () => {
      clearInterval(pollRef.current);
      clearInterval(countdownRef.current);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    clearInterval(countdownRef.current);
    if (session && session.running) {
      countdownRef.current = setInterval(() => {
        setSecondsLeft(s => Math.max(0, s - 1));
      }, 1000);
    }
    return () => clearInterval(countdownRef.current);
  }, [session?.running, session?.vm_id]);

  const setSession = (data) => {
    setSessionState({
      running: true,
      vm_id: data.container_id || data.vm_id,
      course: data.course,
      lab_id: data.lab_id,
      terminalUrl: data.terminalUrl,
      idle_seconds_remaining: data.ttlMinutes ? data.ttlMinutes * 60 : 1800,
    });
    setSecondsLeft(data.ttlMinutes ? data.ttlMinutes * 60 : 1800);
  };

  const clearSession = () => {
    setSessionState(false);
    setSecondsLeft(0);
    clearInterval(countdownRef.current);
  };

  const resumeSession = () => {
    if (session && session.course && session.lab_id) {
      window.location.href = `/labs/${session.course}/${session.lab_id}`;
    }
  };

  return (
    <VMContext.Provider value={{ session, secondsLeft, setSession, clearSession, resumeSession }}>
      {children}
    </VMContext.Provider>
  );
};

export const useVMContext = () => {
  const ctx = useContext(VMContext);
  if (!ctx) throw new Error('useVMContext must be used within VMContextProvider');
  return ctx;
};

export default VMContext;
